import {Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ChartParentType} from '../../statistics/enums/chart-parent-type.enum';
import * as _ from 'lodash';

import * as Highcharts from 'highcharts';
import {LoaderService} from '../../services/common/loader-service/loader.service';
const highchartsExporting = require('highcharts/modules/exporting');
const highchartsOfflineExporting = require('highcharts/modules/offline-exporting');
const highchartsLables = require('highcharts/modules/series-label');
import * as HighchartsBoost from 'highcharts/modules/boost';

HighchartsBoost(Highcharts);
highchartsExporting(Highcharts);
highchartsOfflineExporting(Highcharts);
highchartsLables(Highcharts);

@Component({
  selector: 'arm-highchart',
  templateUrl: './highchart.component.html',
  styleUrls: ['./highchart.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class HighchartComponent implements OnInit {

  constructor(private loaderService: LoaderService) {
  }

  @ViewChild('highChartTpl', {static: true}) highChartTpl: ElementRef;
  @Input() options: any;

  chart: any;

  ngOnInit(): void {
    let chartTimeout;
    this.chart = Highcharts.chart(this.highChartTpl.nativeElement, this.getChartOptions(this.options), this.changeYAxisExtremesDynamically);

    const observer = new ResizeObserver(entries => {
      clearTimeout(chartTimeout);
      chartTimeout = setTimeout(() => {
        const width = entries[0].contentRect.width;
        this.chart.update({
          chart: { width }
        });
      }, 400);
    });

    observer.observe(this.highChartTpl.nativeElement);
  }

  setOptions(newOptions): void {
    if (!_.isNil(this.chart)) {
      this.chart.destroy();
    }
    this.chart = Highcharts.chart(this.highChartTpl.nativeElement, this.getChartOptions(newOptions), this.changeYAxisExtremesDynamically);
  }

  startLoading(): void {
    this.loaderService.showMainContainer();
  }

  stopLoading(): void {
    this.loaderService.hideMainContainer();
  }

  printChart(): void {
    this.chart.print();
  }

  downloadChartAsPdf(): void {
    this.chart.exportChartLocal({type: 'application/pdf'});
  }

  downloadChartAsImage(type): void {
    this.chart.exportChartLocal({type: 'image/' + type});
  }

  changeYAxisExtremesDynamically(chart): void {
    const dataMin = chart.yAxis[0].dataMin;
    let dataMax = chart.yAxis[0].dataMax;

    if (dataMin === 0 && dataMax === 0) {
      dataMax = dataMin + 5;

      chart.yAxis[0].update({
        min: dataMin,
        max: dataMax
      });
    }
  }

  getChartOptions(parameters): any {
    const options: any = {
      chart: {
        type: parameters.type,
        zoomType: 'x',
        panning: true,
        panKey: 'ctrl'
      },
      time: {
        useUTC: false
      },
      title: {
        text: parameters.title.text,
        style: {
          fontSize: parameters.title.fontSize ? parameters.title.fontSize : '18px'
        }
      },
      subtitle: {
        text: parameters.subTitle
      },
      series: parameters.series,
      exporting: {
        enabled: parameters.exporting !== undefined ? parameters.exporting : true
      },
      credits: parameters.lastUpdateTime ?
        {
          href: '',
          text: 'Last update: ' + parameters.lastUpdateTime,
          position: {align: 'left', x: 20},
          style: {fontSize: '12px', cursor: 'default'}
        } : false
    };

    if (parameters.parentType === ChartParentType.LINE) {
      this.addLineOptions(options, parameters);
    } else if (parameters.parentType === ChartParentType.COLUMN) {
      this.addColumnOptions(options, parameters);
    } else {
      this.addStackedColumnOptions(options, parameters);
    }

    if (parameters.series.length === 0) {
      options.xAxis = {
        min: 0,
        max: 10
      };
      options.yAxis = {
        allowDecimals: false,
        min: 0,
        max: 5
      };
      options.series = [{
        showInLegend: false,
        data: []
      }];
      options.chart = {
        marginBottom: 80
      };
    } else if (parameters.limit) {
      options.yAxis = {
        allowDecimals: false,
        plotLines: [{
          color: '#FF0000',
          width: 2,
          value: parameters.limit,
          label: {
            text: 'Limit: ' + parameters.limit,
            align: 'left',
            style: {
              color: '#FF0000'
            }
          }
        }]
      };

      if (!this.isLimitSeriesExist(options.series)) {
        options.series.push({
          showInLegend: false,
          name: 'Limit',
          data: [parameters.limit],
          enableMouseTracking: false,
          type: 'scatter',
          marker: {
            enabled: false
          }
        });
      }
    }
    options.tooltip = {
        formatter(tooltip): any {
            const defaultFormatter = tooltip.defaultFormatter.call(this, tooltip);
            return defaultFormatter[0] + defaultFormatter[1] +
                (_.isNil(this.point.additionalInfo) ? '' :
                    this.point.additionalInfo.map(info => `<span>${info.name}: ${info.value}</span>`).join('<br/>'));
    }
      };

    return options;
  }

  addLineOptions(options, parameters): void {
    options.xAxis = {
      type: 'datetime',
      labels: {
        format: '{value:%H:%M}',
      }
    };

    options.plotOptions = {
      series: {
        pointStart: Date.parse(parameters.startDate),
        pointInterval: parameters.interval,
        animation: parameters.animation,
        stacking: parameters.stacked,
        turboThreshold: 10800,
        boostThreshold: parameters.boost ? 1 : 0
      }
    };

    options.yAxis = {
      allowDecimals: false
    };
  }

  addColumnOptions(options, parameters): void {
    options.xAxis = {
      type: 'category'
    };

    options.plotOptions = {
      series: {
        colorByPoint: true,
        borderRadius: 6,
        pointWidth: 20,
        animation: parameters.animation,
        stacking: parameters.stacked
      },
      pie: {
        allowPointSelect: true,
        cursor: 'pointer'
      }
    };
  }

  addStackedColumnOptions(options, parameters): void {
    options.xAxis = {
      type: 'category'
    };

    options.plotOptions = {
      series: {
        borderRadius: 6,
        pointWidth: 20,
        animation: parameters.animation,
        stacking: parameters.stacked
      }
    };
  }

  private isLimitSeriesExist(series): boolean {
    return !!series.find(seriesItem => seriesItem.name === 'Limit');
  }
}
